import { useEffect, useState } from 'react';
import { Box, Divider, IconButton, Modal, Stack, TextField, Tooltip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import CustomText from 'components/common/CustomText/CustomText';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import { colors } from 'styles/colors';
import { RawProductContents, RawProductContentsPatch } from 'services/rawProductService';
import { usePressToSave } from './usePressToSave';

interface Props {
  contents: RawProductContents | null;
  open: boolean;
  productName: string;
  isSaving: boolean;
  onClose: () => void;
  onSave: (contents: RawProductContentsPatch) => void;
}

// One thing out of the pack, both languages together. The two lists name the
// same things in the same order, so they are edited as one row: a thing removed
// from one side and left on the other is how they stop lining up.
interface EntryDraft {
  nameEn: string;
  nameAr: string;
  amount: string;
  unitEn: string;
  unitAr: string;
  analysisEn: string;
  analysisAr: string;
}

interface ContentsDraft {
  introEn: string;
  introAr: string;
  entries: EntryDraft[];
}

function toDraft(contents: RawProductContents | null): ContentsDraft {
  const en = contents?.en?.contents ?? [];
  const ar = contents?.ar?.contents ?? [];
  const rows = Math.max(en.length, ar.length);

  return {
    introEn: contents?.en?.introduction ?? '',
    introAr: contents?.ar?.introduction ?? '',
    entries: Array.from({ length: rows }, (_value, row) => ({
      nameEn: en[row]?.contentName ?? '',
      nameAr: ar[row]?.contentName ?? '',
      // Held as text so a half-typed number is not blanked while it is being
      // typed. The count is one fact about the thing, not one per language.
      amount:
        en[row]?.amount !== null && en[row]?.amount !== undefined
          ? String(en[row]?.amount)
          : ar[row]?.amount !== null && ar[row]?.amount !== undefined
            ? String(ar[row]?.amount)
            : '',
      unitEn: en[row]?.amountUnit ?? '',
      unitAr: ar[row]?.amountUnit ?? '',
      analysisEn: en[row]?.analysis ?? '',
      analysisAr: ar[row]?.analysis ?? '',
    })),
  };
}

const EMPTY_ENTRY: EntryDraft = {
  nameEn: '',
  nameAr: '',
  amount: '',
  unitEn: '',
  unitAr: '',
  analysisEn: '',
  analysisAr: '',
};

export default function ContentsModal({
  contents,
  open,
  productName,
  isSaving,
  onClose,
  onSave,
}: Props) {
  const [draft, setDraft] = useState<ContentsDraft>(toDraft(null));
  const { saving, press } = usePressToSave(isSaving, open);

  // Opened on what the row holds every time rather than on whatever was left
  // here last: an abandoned edit that came back on the next open would read as a
  // stored list.
  useEffect(() => {
    if (!open) return;
    setDraft(toDraft(contents));
  }, [open, contents]);

  const changed = JSON.stringify(draft) !== JSON.stringify(toDraft(contents));

  const patchEntry = (row: number, next: Partial<EntryDraft>) =>
    setDraft((existing) => ({
      ...existing,
      entries: existing.entries.map((entry, position) =>
        position === row ? { ...entry, ...next } : entry,
      ),
    }));

  const field = (
    label: string,
    value: string,
    onChange: (next: string) => void,
    rtl = false,
    multiline = false,
  ) => (
    <Stack spacing={0.5}>
      <CustomText text={label} fontSize={11} fontWeight={600} color={colors.gray} />
      <TextField
        fullWidth
        size="small"
        multiline={multiline}
        value={value}
        disabled={saving}
        onChange={(event) => onChange(event.target.value)}
        placeholder="—"
        inputProps={rtl ? { dir: 'rtl' } : undefined}
      />
    </Stack>
  );

  return (
    <Modal open={open} onClose={saving ? undefined : onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 3,
          width: 900,
          maxWidth: '94vw',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <CustomText text="Edit product contents" fontWeight={700} fontSize={18} />
        <Box mt={0.5}>
          <CustomText text={productName} fontSize={13} color={colors.gray} />
        </Box>

        {/* The sentence that says what the pack holds, above the things it
            holds, in the two columns the card uses. */}
        <Box
          mt={2}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            columnGap: 2,
            rowGap: 1.5,
          }}
        >
          {field('Introduction (EN)', draft.introEn, (next) =>
            setDraft((existing) => ({ ...existing, introEn: next })), false, true)}
          {field('Introduction (AR)', draft.introAr, (next) =>
            setDraft((existing) => ({ ...existing, introAr: next })), true, true)}
        </Box>

        {draft.entries.map((entry, row) => (
          <Box key={`content-entry-${row}`} mt={2.5}>
            <Divider sx={{ mb: 2 }} />
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <CustomText
                text={`Item ${row + 1}`}
                fontSize={14}
                fontWeight={600}
                color={colors.primary}
              />
              <Tooltip title="Remove this item">
                <IconButton
                  size="small"
                  disabled={saving}
                  onClick={() =>
                    setDraft((existing) => ({
                      ...existing,
                      entries: existing.entries.filter((_entry, position) => position !== row),
                    }))
                  }
                >
                  <DeleteOutlineIcon sx={{ fontSize: 16, color: colors.gray }} />
                </IconButton>
              </Tooltip>
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                columnGap: 2,
                rowGap: 1.5,
              }}
            >
              {field('Name (EN)', entry.nameEn, (next) => patchEntry(row, { nameEn: next }))}
              {field('Name (AR)', entry.nameAr, (next) => patchEntry(row, { nameAr: next }), true)}
              {/* One count, two words for it: how many the pack states is a fact
                  about the pack, while what one of them is called is written for
                  each reader. An empty count is the pack printing none. */}
              {field('Amount', entry.amount, (next) => patchEntry(row, { amount: next }))}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  columnGap: 2,
                }}
              >
                {field('Unit (EN)', entry.unitEn, (next) => patchEntry(row, { unitEn: next }))}
                {field('Unit (AR)', entry.unitAr, (next) => patchEntry(row, { unitAr: next }), true)}
              </Box>
              {field(
                'What it means (EN)',
                entry.analysisEn,
                (next) => patchEntry(row, { analysisEn: next }),
                false,
                true,
              )}
              {field(
                'What it means (AR)',
                entry.analysisAr,
                (next) => patchEntry(row, { analysisAr: next }),
                true,
                true,
              )}
            </Box>
          </Box>
        ))}

        <Box mt={2}>
          <CustomButton
            text="Add item"
            variant="outlined"
            height={32}
            icon={<AddIcon sx={{ fontSize: 16 }} />}
            disabled={saving}
            onClick={() =>
              setDraft((existing) => ({ ...existing, entries: [...existing.entries, EMPTY_ENTRY] }))
            }
          />
        </Box>

        <Box mt={1.5}>
          <CustomText
            text="What the buyer takes out of the pack, and what having it means for them. An empty list says this pack holds nothing separate - one product in its own container."
            fontSize={11}
            color={colors.gray}
          />
        </Box>

        <Stack direction="row" justifyContent="flex-end" spacing={2} mt={3}>
          <CustomButton
            text="Cancel"
            variant="outlined"
            height={40}
            disabled={saving}
            onClick={onClose}
          />
          <CustomButton
            text="Save"
            height={40}
            isLoading={saving}
            disabled={!changed}
            onClick={() => press(() => {
              // An item with no name on either side is one the reviewer emptied
              // rather than one they wrote.
              const kept = draft.entries.filter(
                (entry) => entry.nameEn.trim() || entry.nameAr.trim(),
              );
              const amountOf = (entry: EntryDraft) => {
                const trimmed = entry.amount.trim();
                if (!trimmed) return null;
                const parsed = Number(trimmed);
                return Number.isFinite(parsed) ? parsed : null;
              };

              onSave({
                en: {
                  introduction: draft.introEn.trim(),
                  contents: kept.map((entry) => ({
                    contentName: entry.nameEn.trim(),
                    amount: amountOf(entry),
                    amountUnit: entry.unitEn.trim(),
                    analysis: entry.analysisEn.trim(),
                  })),
                },
                ar: {
                  introduction: draft.introAr.trim(),
                  contents: kept.map((entry) => ({
                    contentName: entry.nameAr.trim(),
                    amount: amountOf(entry),
                    amountUnit: entry.unitAr.trim(),
                    analysis: entry.analysisAr.trim(),
                  })),
                },
              });
            })}
          />
        </Stack>
      </Box>
    </Modal>
  );
}
